import api from '@/services/api';

const BASE = '/v2/brain';

export type BlueprintType =
  | 'job'
  | 'icp'
  | 'pipeline'
  | 'evaluation'
  | 'email'
  | 'question-set';
export type KnowledgeAppliesTo = 'icp_agent' | 'cv_screener' | 'pipeline_agent' | 'global';

export const BLUEPRINT_TYPE_LABELS: Record<BlueprintType, string> = {
  email: 'Email Templates',
  job: 'Job Templates',
  icp: 'ICP Templates',
  pipeline: 'Pipeline Presets',
  evaluation: 'Evaluation Frameworks',
  'question-set': 'Question Sets',
};

export const BLUEPRINT_TYPE_SINGULAR: Record<BlueprintType, string> = {
  email: 'Email Template',
  job: 'Job Template',
  icp: 'ICP Template',
  pipeline: 'Pipeline Preset',
  evaluation: 'Evaluation Framework',
  'question-set': 'Question Set',
};

export const BLUEPRINT_TYPE_ORDER: BlueprintType[] = [
  'email',
  'job',
  'icp',
  'pipeline',
  'evaluation',
  'question-set',
];

/** "custom-field" is a virtual sub-item that routes to the real CustomFields impl. */
export type BrainBlueprintSlug = BlueprintType | 'custom-field';

export const BLUEPRINT_SLUG_ORDER: BrainBlueprintSlug[] = [
  ...BLUEPRINT_TYPE_ORDER,
  'custom-field',
];

export const BLUEPRINT_SLUG_LABELS: Record<BrainBlueprintSlug, string> = {
  ...BLUEPRINT_TYPE_LABELS,
  'custom-field': 'Custom Fields',
};

export const KNOWLEDGE_APPLIES_ORDER: KnowledgeAppliesTo[] = [
  'global',
  'icp_agent',
  'cv_screener',
  'pipeline_agent',
];

export const KNOWLEDGE_APPLIES_LABELS: Record<KnowledgeAppliesTo, string> = {
  global: 'Global',
  icp_agent: 'ICP Agent',
  cv_screener: 'CV Screener',
  pipeline_agent: 'Pipeline Agent',
};

/** URL slugs for knowledge appliesTo (using hyphens instead of underscores). */
export const KNOWLEDGE_APPLIES_SLUGS: Record<KnowledgeAppliesTo, string> = {
  global: 'global',
  icp_agent: 'icp-agent',
  cv_screener: 'cv-screener',
  pipeline_agent: 'pipeline-agent',
};

export function knowledgeAppliesFromSlug(slug: string | undefined): KnowledgeAppliesTo | null {
  const entries = Object.entries(KNOWLEDGE_APPLIES_SLUGS) as [KnowledgeAppliesTo, string][];
  const found = entries.find(([, s]) => s === slug);
  return found?.[0] ?? null;
}

/** Three-tier scope. Blueprints support 'workspace' | 'org' only. */
export type BrainScope = 'personal' | 'workspace' | 'org';

export interface Blueprint {
    _id: string;
    organizationId: string;
    workspaceId: string | null;
    name: string;
    type: BlueprintType;
    description: string;
    payload: Record<string, unknown>;
    order: number;
    createdAt: string;
    updatedAt: string;
}

export interface KnowledgeRule {
    _id: string;
    organizationId: string;
    workspaceId: string | null;
    userId: string | null;
    name: string;
    trigger: string;
    instruction: string;
    appliesTo: KnowledgeAppliesTo;
    /** Free-form user-defined category for grouping ("hiring rubric", "tone", …) */
    genre: string;
    isActive: boolean;
    order: number;
    createdAt: string;
    updatedAt: string;
}

/** Derive an item's scope tier from its userId / workspaceId fields. */
export function scopeOf(item: { userId?: string | null; workspaceId?: string | null }): BrainScope {
    if (item.userId) return 'personal';
    if (item.workspaceId) return 'workspace';
    return 'org';
}

export const brainApi = {
    // ─── Blueprints (workspace + org only) ───────────────────────────────────

    listBlueprints: (params?: { type?: BlueprintType }) =>
        api.get<Blueprint[]>(`${BASE}/blueprints`, { params }),

    createBlueprint: (data: {
        name: string;
        type: BlueprintType;
        description?: string;
        payload?: Record<string, unknown>;
        order?: number;
        scope: 'workspace' | 'org';
        /** Required when scope === 'workspace'. Pick from the caller's accessible workspaces. */
        workspaceId?: string;
    }) => api.post<Blueprint>(`${BASE}/blueprints`, data),

    updateBlueprint: (
        id: string,
        data: {
            name?: string;
            description?: string;
            payload?: Record<string, unknown>;
            order?: number;
        },
    ) => api.patch<Blueprint>(`${BASE}/blueprints/${id}`, data),

    deleteBlueprint: (id: string) => api.delete(`${BASE}/blueprints/${id}`),

    // ─── Knowledge Rules (personal + workspace + org) ────────────────────────

    listKnowledgeRules: (params?: { appliesTo?: KnowledgeAppliesTo }) =>
        api.get<KnowledgeRule[]>(`${BASE}/knowledge`, { params }),

    createKnowledgeRule: (data: {
        name: string;
        trigger?: string;
        instruction: string;
        appliesTo?: KnowledgeAppliesTo;
        genre?: string;
        isActive?: boolean;
        order?: number;
        scope: BrainScope;
        /** Required when scope === 'workspace'. Pick from the caller's accessible workspaces. */
        workspaceId?: string;
    }) => api.post<KnowledgeRule>(`${BASE}/knowledge`, data),

    updateKnowledgeRule: (
        id: string,
        data: {
            name?: string;
            trigger?: string;
            instruction?: string;
            appliesTo?: KnowledgeAppliesTo;
            genre?: string;
            isActive?: boolean;
            order?: number;
        },
    ) => api.patch<KnowledgeRule>(`${BASE}/knowledge/${id}`, data),

    deleteKnowledgeRule: (id: string) => api.delete(`${BASE}/knowledge/${id}`),
};
